import { useState, type FormEvent } from 'react';
import { basispuntenNaarProcent, procentNaarBasispunten } from '@qc/core';
import { Knop } from '@qc/ui';
import { Veld } from '../auth/Veld.js';
import type { EvenementBeeld } from './useEvenement.js';

export interface KramenBeheerProps {
  beeld: EvenementBeeld;
}

const STATUSTEKST: Record<string, string> = {
  uitgenodigd: 'uitgenodigd',
  aangemeld: 'aangemeld',
  goedgekeurd: 'goedgekeurd',
  actief: 'draait',
  gepauzeerd: 'gepauzeerd',
  afgesloten: 'afgesloten',
};

export function KramenBeheer({ beeld }: KramenBeheerProps) {
  const [naam, setNaam] = useState('');
  const [email, setEmail] = useState('');
  const [commissie, setCommissie] = useState('10');
  const [fout, setFout] = useState<string | null>(null);
  const [nieuweCode, setNieuweCode] = useState<{
    naam: string;
    code: string;
    gemaild: boolean;
    email: string;
  } | null>(null);
  /** Per kraamdeelname wat er van "Uitnodiging mailen" terugkwam. */
  const [mailmelding, setMailmelding] = useState<Record<string, { ok: boolean; tekst: string }>>(
    {},
  );

  async function nodigUit(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    setFout(null);

    // Percentages zijn integers in basispunten (12,5% wordt 1250); die
    // omrekening staat in packages/core, want ze loopt via de commissie zo in
    // het geld door.
    let basispunten = 0;
    try {
      basispunten = procentNaarBasispunten(commissie);
    } catch (oorzaak) {
      setFout(oorzaak instanceof Error ? oorzaak.message : 'Vul een geldig percentage in.');
      return;
    }

    const uitslag = await beeld.nodigKraamUit(naam, email, basispunten);
    if (uitslag !== null) {
      setNieuweCode({ naam, code: uitslag.code, gemaild: uitslag.gemaild, email });
      setNaam('');
      setEmail('');
    }
  }

  return (
    <div>
      <h2 className="mb-2 text-xl font-bold">Kraam uitnodigen</h2>
      <form
        onSubmit={(e) => void nodigUit(e)}
        className="mb-6 grid gap-4 rounded-tegel border border-rand bg-vlak p-6 sm:grid-cols-3"
      >
        <Veld
          label="Naam van de kraam"
          required
          value={naam}
          onChange={(e) => setNaam(e.target.value)}
        />
        <Veld
          label="E-mailadres"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Veld
          label="Commissie in procenten"
          value={commissie}
          onChange={(e) => setCommissie(e.target.value)}
          toelichting="Bijvoorbeeld 10 of 12,5."
        />
        {fout !== null && <p className="text-lg text-fout sm:col-span-3">{fout}</p>}
        <div className="sm:col-span-3">
          <Knop nadruk="hoofd" type="submit">
            Uitnodigen
          </Knop>
        </div>
      </form>

      {nieuweCode !== null && (
        <div className="mb-6 rounded-tegel border border-accent bg-vlak p-6">
          <p className="text-tekst-zacht">Inlogcode voor {nieuweCode.naam}</p>
          <p className="my-2 text-4xl font-bold tracking-wider text-accent">{nieuweCode.code}</p>
          <p className="text-base text-tekst-zacht">
            Met deze code stuurt de kraam zijn gegevens en menukaart in, en logt hij straks in op
            het toestel.
          </p>
          <p className={`mt-2 text-base ${nieuweCode.gemaild ? 'text-goed' : 'text-waarschuwing'}`}>
            {nieuweCode.gemaild
              ? `De uitnodiging met deze code is gemaild naar ${nieuweCode.email}.`
              : 'Er is geen uitnodiging gemaild. Geef de code zelf door aan de kraam.'}
          </p>
        </div>
      )}

      <h2 className="mb-2 text-xl font-bold">Kramen op dit evenement</h2>
      <ul className="grid gap-3">
        {beeld.kramen.map((kraam) => (
          <li
            key={kraam.deelname.id}
            className="flex flex-wrap items-center justify-between gap-4 rounded-tegel border border-rand bg-vlak p-5"
          >
            <div className="min-w-0">
              <p className="truncate text-xl font-semibold">{kraam.kraamnaam}</p>
              <p className="text-tekst-zacht">
                {STATUSTEKST[kraam.deelname.status] ?? kraam.deelname.status} ·{' '}
                {basispuntenNaarProcent(kraam.deelname.commissie_bp)}% commissie
                {kraam.deelname.inlogcode != null ? ` · code ${kraam.deelname.inlogcode}` : ''}
              </p>
              {mailmelding[kraam.deelname.id] !== undefined && (
                <p
                  className={
                    mailmelding[kraam.deelname.id]?.ok === true ? 'text-goed' : 'text-waarschuwing'
                  }
                >
                  {mailmelding[kraam.deelname.id]?.tekst}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Knop
                nadruk="gewoon"
                className="px-4 text-base"
                onClick={() => {
                  void beeld.mailUitnodiging(kraam.deelname.id).then((uitslag) => {
                    setMailmelding((huidig) => ({
                      ...huidig,
                      [kraam.deelname.id]: { ok: uitslag.ok, tekst: uitslag.melding },
                    }));
                  });
                }}
              >
                Uitnodiging mailen
              </Knop>
              <Knop
                nadruk="gewoon"
                className="px-4 text-base"
                onClick={() => {
                  const antwoord = window.prompt(
                    `Commissie voor ${kraam.kraamnaam} in procenten`,
                    basispuntenNaarProcent(kraam.deelname.commissie_bp),
                  );
                  if (antwoord === null) return;
                  try {
                    void beeld.zetCommissie(kraam.deelname.id, procentNaarBasispunten(antwoord));
                  } catch (oorzaak) {
                    setFout(
                      oorzaak instanceof Error ? oorzaak.message : 'Dat percentage kan niet.',
                    );
                  }
                }}
              >
                Commissie
              </Knop>
              {kraam.deelname.status === 'gepauzeerd' ? (
                <Knop
                  nadruk="hoofd"
                  className="px-4 text-base"
                  onClick={() => void beeld.zetKraamstatus(kraam.deelname.id, true)}
                >
                  Weer starten
                </Knop>
              ) : (
                <Knop
                  nadruk="gevaar"
                  className="px-4 text-base"
                  onClick={() => void beeld.zetKraamstatus(kraam.deelname.id, false)}
                >
                  Pauzeren
                </Knop>
              )}
            </div>
          </li>
        ))}
        {beeld.kramen.length === 0 && (
          <li className="text-tekst-zacht">Er doet nog geen kraam mee.</li>
        )}
      </ul>
    </div>
  );
}
